import Layout from 'components/layout/Layout'
import Registration from 'features/registration/Registration'
import type { RegistrationProps } from 'features/registration/types'
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import i18nConfig from 'utils/i18nConfig'
import { Language } from 'types/language'
import { DEFAULT_NAMESPACES, PRODUCERS_NAMESPACES, SIMULATION_NAMESPACES } from 'constants/i18n'
import { useTranslation } from 'next-i18next/pages'
import { useStoreSelector } from 'hooks/store'
import type { SeoProps } from 'components/seo/types'
import { useRouter } from 'next/router'
import { cmsLib, producersLib } from 'lib'
import { log } from 'utils/logging'
import { Criticality, Flow, Severity } from 'types/logging'
import { SIMULATION_TYPES } from '@boltenergy-be/shared'

const RegistrationPage = (props: RegistrationProps) => {
  // Redux
  const { propositions, inputs } = useStoreSelector((store) => store.customer)

  // i18n
  const { t } = useTranslation('producer')

  // Router
  const { locale } = useRouter()

  // Constants
  const isExpressRegistration = inputs.simulation.chosenSimulationType === SIMULATION_TYPES.express
  const SEO: SeoProps =
    isExpressRegistration && !propositions?.simulatedPropositions
      ? undefined
      : {
          title: t('seoTitle', { name: props.producer.name[locale] })
        }

  return (
    <Layout seo={SEO} hideHeader hideFooter>
      <Registration {...props} />
    </Layout>
  )
}

export const getStaticProps = async ({ params, locale }) => {
  // Fetch the needed data
  const producer = await producersLib.getProducerBySlug(params.slug)

  // Check if producer was fetched
  if (!producer) {
    log({
      error: 'Producer registration page not found',
      criticality: Criticality.HIGH,
      severity: Severity.WARNING,
      identifier: `[${Flow.NOT_FOUND}:producer-registration-page]`,
      metaData: { producer: { slug: params.slug } }
    })

    return {
      notFound: true,
      revalidate: 10 // re-generate page when a request comes in, once every 10 seconds
    }
  }

  const activeAction = await cmsLib.getActiveReferralAction(locale)

  return {
    props: {
      producer,
      activeAction,
      ...(await serverSideTranslations(
        locale,
        [...DEFAULT_NAMESPACES, ...PRODUCERS_NAMESPACES, ...SIMULATION_NAMESPACES, 'registration'],
        i18nConfig
      ))
    },
    revalidate: 600 // re-generate page when a request comes in, once every 10 minutes
  }
}

export const getStaticPaths = async () => {
  const producers = await producersLib.getProducers({ language: Language.DUTCH, params: { enabled: true } })
  const paths = []

  // Add both 'nl' and 'fr' path of each existing producer to paths
  producers.forEach((producer) => {
    const producerPaths = [
      { locale: 'nl', params: { slug: producer.slug } },
      { locale: 'fr', params: { slug: producer.slug } }
    ]
    paths.push(...producerPaths)
  })

  return {
    fallback: 'blocking',
    paths // will server-render pages on-demand if the path does not exist
  }
}

export default RegistrationPage
